import React, {useState, useEffect} from "react";
import MintDBLoader from "./MintDBLoader.tsx";

function Sources(){
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total_disorders: 0,
        total_targets: 0,
        total_variants: 0,
        total_drugs: 0
    });

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch("/api/getData?type=stats");
                const data = await response.json();
                setStats(data);
            } catch (err) {
                console.error("Error fetching stats:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    const sources = [
        {
            category: "Drug Information",
            name: "Drugs.com",
            description: "FDA approved drug list and comprehensive drug information",
            url: "https://www.drugs.com",
            dataType: "Drug names, classifications, FDA approval status"
        },
        {
            category: "Drug Information",
            name: "UniProt",
            description: "Position Classifications",
            url: "https://www.uniprot.org/",
            dataType: "."
        },
        {
            category: "Drug Targets",
            name: "DrugBank",
            description: "Comprehensive database of drug and drug target information",
            url: "https://go.drugbank.com",
            dataType: "Drug-target interactions, protein targets, pharmacological data"
        },
        {
            category: "Genetic Variants",
            name: "gnomAD",
            description: "Genome Aggregation Database - population genetics data",
            url: "https://gnomad.broadinstitute.org",
            dataType: "RSIDs, genetic mutations, population frequency data"
        },
        {
            category: "Variant Classification",
            name: "AlphaMissense",
            description: "AI-powered predictions of missense variant pathogenicity",
            url: "https://alphamissense.hegelab.org",
            dataType: "Pathogenicity classifications for genetic variants"
        },
        {
            category: "Brain Expression",
            name: "Human Protein Atlas",
            description: "Tissue-specific protein expression patterns",
            url: "https://www.proteinatlas.org",
            dataType: "Brain tissue expression data, protein localization"
        },
        {
            category: "Ligand Information",
            name: "IUPHAR/BPS Guide to Pharmacology",
            description: "Comprehensive resource for pharmacological data",
            url: "https://www.guidetopharmacology.org",
            dataType: "Ligand-receptor interactions, endogenous ligands, pharmacology"
        },
        {
            category: "Protein Structure",
            name: "RCSB Protein Data Bank",
            description: "Repository of 3D structural data of biological macromolecules",
            url: "https://www.rcsb.org",
            dataType: "3D protein structures, structural annotations, PDB files"
        }
    ];

    if (loading) {
        return <MintDBLoader />;
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#FBFBFC',
            paddingBottom: '3rem'
        }}>
            <div className="container">
                {/* Header Section */}
                <div className="row justify-content-center text-center" style={{paddingTop: '4rem', paddingBottom: '2rem'}}>
                    <div className="col-lg-8">

                        <h1 style={{
                            fontSize: '3rem',
                            fontWeight: '700',
                            color: '#1A2940',
                            marginBottom: '1rem'
                        }}>
                            MintDB Sources
                        </h1>
                        <p style={{
                            fontSize: '1rem',
                            color: '#64748b',
                            lineHeight: '1.6',
                            maxWidth: '600px',
                            margin: '0 auto'
                        }}>
                            Our database integrates high-quality data from multiple authoritative sources to provide
                            comprehensive information about drug targets, genetic variants, and their clinical relevance.
                        </p>
                    </div>
                </div>

                {/* Statistics Section */}
                <div className="row justify-content-center" style={{marginTop: '2rem', marginBottom: '3rem'}}>
                    <div className="col-lg-10">
                        <div className="row g-3">
                            <div className="col-md-3 col-sm-6">
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    border: '1px solid #eef2f6'
                                }}>
                                    <div style={{
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        color: '#1A2940',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {stats.total_disorders || 0}
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#64748b',
                                        fontWeight: '600'
                                    }}>
                                        Disorders
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 col-sm-6">
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    border: '1px solid #eef2f6'
                                }}>
                                    <div style={{
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        color: '#1A2940',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {stats.total_targets || 0}
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#64748b',
                                        fontWeight: '600'
                                    }}>
                                        Drug Targets
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 col-sm-6">
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    border: '1px solid #eef2f6'
                                }}>
                                    <div style={{
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        color: '#1A2940',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {stats.total_variants ? stats.total_variants.toLocaleString() : 0}
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#64748b',
                                        fontWeight: '600'
                                    }}>
                                        Genetic Variants
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3 col-sm-6">
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '1.5rem',
                                    textAlign: 'center',
                                    border: '1px solid #eef2f6'
                                }}>
                                    <div style={{
                                        fontSize: '2.5rem',
                                        fontWeight: 'bold',
                                        color: '#1A2940',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {stats.total_drugs || 0}
                                    </div>
                                    <div style={{
                                        fontSize: '0.9rem',
                                        color: '#64748b',
                                        fontWeight: '600'
                                    }}>
                                        Drugs
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <hr style={{
                    borderColor: '#eef2f6',
                    margin: '2rem 0'
                }}></hr>

                {/* Sources List */}
                <div className="row justify-content-center">
                    <div className="col-12">
                        <h4 style={{
                            color: '#1A2940',
                            fontWeight: 'bold',
                            marginBottom: '2rem',
                            textAlign: 'center'
                        }}>
                            Data Sources Overview
                        </h4>

                        <div style={{overflowX: 'auto'}}>
                            <table style={{
                                width: '100%',
                                backgroundColor: 'white',
                                borderCollapse: 'collapse'
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: '#1A2940',
                                        color: 'white'
                                    }}>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem',
                                            width: '60px'
                                        }}>#</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }}>Category</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }}>Source</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }}>Description</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }}>Data Used</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }}>Link</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sources.map((source, index) => (
                                        <tr key={index} style={{
                                            borderBottom: '1px solid #eef2f6'
                                        }}>
                                            <td style={{
                                                padding: '1rem',
                                                textAlign: 'center',
                                                color: '#1A2940',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem'
                                            }}>{index + 1}</td>
                                            <td style={{
                                                padding: '1rem',
                                                color: '#64748b',
                                                fontSize: '0.9rem'
                                            }}>{source.category}</td>
                                            <td style={{
                                                padding: '1rem',
                                                color: '#1A2940',
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem'
                                            }}>{source.name}</td>
                                            <td style={{
                                                padding: '1rem',
                                                color: '#64748b',
                                                fontSize: '0.9rem',
                                                lineHeight: '1.5'
                                            }}>{source.description}</td>
                                            <td style={{
                                                padding: '1rem',
                                                color: '#64748b',
                                                fontSize: '0.85rem',
                                                lineHeight: '1.5'
                                            }}>{source.dataType}</td>
                                            <td style={{
                                                padding: '1rem',
                                                textAlign: 'center'
                                            }}>
                                                <a
                                                    href={source.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: '#60a5fa',
                                                        textDecoration: 'none',
                                                        fontWeight: '600',
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    Visit ↗
                                                </a>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Sources;